import { Data, Effect, Match, Option, Schema } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';

import { PageOf, type Page } from '@vs-shop/domain/pagination';
import { reactivityKeys } from '../../atom/reactivityKeys';
import { ProductListing } from '../../capability/productListing/ProductListing';
import { FacetSet } from '../../capability/productListing/facetSet';
import {
  ListingCacheKey,
  type ListingPageRequest,
  type ListingRequest,
} from '../../capability/productListing/listingRequestKey';
import { ProductSummary } from '../../capability/productSchema';
import type { resolveListingCacheOptions } from './ListingCacheOptions';
import { makeListingRetention, RetainedListingEntry } from './ListingRetention';
import { dehydrateValue } from './dehydrateValue';

export type ListingQuery<A, E> = {
  readonly current: Atom.Atom<AsyncResult.AsyncResult<A, E>>;
  /** Refreshes the query only after its current read finishes. */
  readonly retry: Atom.Writable<void, void>;
  /** Restarts the query for explicit failed-page recovery. */
  readonly refresh: Atom.Writable<void, void>;
};

class SessionKey<A> extends Data.Class<{
  readonly key: ListingCacheKey<A>;
  readonly epoch: number;
}> {}
const inSession = <A>(key: ListingCacheKey<A>, epoch: number): SessionKey<A> =>
  new SessionKey({ key, epoch });
/** Couples hydration, native SWR, refresh commands, and retained leases for each query. */
export const makeListingQueries = <ER>(deps: {
  readonly runtime: Atom.AtomRuntime<ProductListing, ER>;
  readonly keyPrefix: string;
  readonly settings: ReturnType<typeof resolveListingCacheOptions>;
}) => {
  const settings = deps.settings;
  const contextEpoch = Atom.make((get) =>
    Option.match(get.self<number>(), {
      onNone: () => 0,
      onSome: (previous) => previous + 1,
    })
  ).pipe(Atom.withReactivity([reactivityKeys.authToken, reactivityKeys.customer]), Atom.keepAlive);

  const queryActivated = Atom.family((_base: Atom.Atom<unknown>) =>
    Atom.make(false).pipe(Atom.setIdleTTL(0))
  );
  const retention = Atom.make((get) => {
    get(contextEpoch);
    return makeListingRetention(get.registry, settings);
  }).pipe(Atom.keepAlive);
  const retain = Atom.family((atom: Atom.Atom<unknown>) =>
    Atom.make((get) =>
      get.result(retention).pipe(
        Effect.flatMap((service) =>
          service.retain(
            new RetainedListingEntry({
              atom,
              activated: queryActivated(atom),
            })
          )
        )
      )
    ).pipe(Atom.setIdleTTL(0))
  );

  /** Preserves hydration and cold-load errors before SWR handles later activations. */
  const sessionQuery = <A, E>(base: Atom.Atom<AsyncResult.AsyncResult<A, E>>) => {
    const activated = queryActivated(base);
    const swr = base.pipe(Atom.swr({ staleTime: settings.staleTime }));
    return Atom.transform(
      base,
      (get) => {
        get(retain(base));
        const target = Match.value(get.once(activated)).pipe(
          Match.when(true, () => swr),
          Match.when(false, () => base),
          Match.exhaustive
        );
        get.set(activated, true);
        get.subscribe(target, (value) => get.setSelf(value));
        return get.once(target);
      },
      { initialValueTarget: base }
    );
  };

  const makeQueryFamily = <K, A, E>(
    baseFor: (key: K) => Atom.Atom<AsyncResult.AsyncResult<A, E>>
  ) => {
    const current = Atom.family((key: K) => sessionQuery(baseFor(key)));
    const refresh = Atom.family((key: K) => {
      const base = baseFor(key);
      const fn = Atom.fnSync((_input: void, get) => {
        get.refresh(base);
        get(base);
      });
      return Atom.writable(
        () => undefined,
        (context, _input: void) => {
          context.set(fn, undefined);
          context.get(fn);
        }
      );
    });
    const retry = Atom.family((key: K) => {
      const base = baseFor(key);
      const fn = Atom.fnSync((_input: void, get) => {
        Match.value(get(base).waiting).pipe(
          Match.when(false, () => {
            get.refresh(base);
            get(base);
          }),
          Match.when(true, () => {}),
          Match.exhaustive
        );
      });
      return Atom.writable(
        () => undefined,
        (context, _input: void) => {
          context.set(fn, undefined);
          context.get(fn);
        }
      );
    });
    // Registry mounts retain atoms; descriptors do not own atom identity.
    return (key: K): ListingQuery<A, E> => ({
      current: current(key),
      retry: retry(key),
      refresh: refresh(key),
    });
  };
  // Hydration encodes successful results only.
  // Schema.Any preserves the live error slot without an error codec.
  const productsResultSchema = AsyncResult.Schema({
    success: PageOf(ProductSummary),
    error: Schema.Any,
  });
  const facetsResultSchema = AsyncResult.Schema({ success: FacetSet, error: Schema.Any });

  // ListingCacheKey equality shares one atom between equal requests.
  // The key supplies the request and the hydration identifier.
  const productsBaseAtom = Atom.family((session: SessionKey<ListingPageRequest>) =>
    deps.runtime
      .atom(
        Effect.gen(function* () {
          const listing = yield* ProductListing;
          return yield* listing.products(session.key.request);
        })
      )
      .pipe(
        Atom.setIdleTTL(0),
        Atom.serializable({
          key: `${deps.keyPrefix}/products/${session.key.id}/${session.epoch}`,
          schema: productsResultSchema,
        })
      )
  );
  const facetsBaseAtom = Atom.family((session: SessionKey<ListingRequest>) =>
    deps.runtime
      .atom(
        Effect.gen(function* () {
          const listing = yield* ProductListing;
          return yield* listing.facets(session.key.request);
        })
      )
      .pipe(
        Atom.setIdleTTL(0),
        Atom.serializable({
          key: `${deps.keyPrefix}/facets/${session.key.id}/${session.epoch}`,
          schema: facetsResultSchema,
        })
      )
  );

  const productsQuery = makeQueryFamily(productsBaseAtom);
  const facetsQuery = makeQueryFamily(facetsBaseAtom);

  return {
    epoch: contextEpoch,
    retain,
    products: (request: ListingPageRequest, epoch: number) =>
      productsQuery(inSession(ListingCacheKey.products(request), epoch)),
    facets: (request: ListingRequest, epoch: number) =>
      facetsQuery(inSession(ListingCacheKey.facets(request), epoch)),
    dehydrateProducts: (request: ListingPageRequest, value: Page<ProductSummary>) =>
      dehydrateValue(
        productsBaseAtom(inSession(ListingCacheKey.products(request), 0)),
        AsyncResult.success(value)
      ),
    dehydrateFacets: (request: ListingRequest, value: FacetSet) =>
      dehydrateValue(
        facetsBaseAtom(inSession(ListingCacheKey.facets(request), 0)),
        AsyncResult.success(value)
      ),
  };
};
